'use client';

import type { CheckboxCheckedChangeDetails } from '@chakra-ui/react';
import {
	useState,  useMemo, useCallback,
} from 'react';

export const useSelectTableHandler = <T extends number | string>(allIds: T[]) => {
	const [ selectedIds, setSelectedIds ] = useState<Set<T>>(new Set());

	const selected = useMemo(() => [ ...selectedIds ], [ selectedIds ]);
	const isSelected = useCallback((id: T) => selectedIds.has(id), [ selectedIds ]);

	const isIndeterminate = selectedIds.size > 0 && selectedIds.size < allIds.length;
	const isAllSelected = selectedIds.size === allIds.length && allIds.length > 0;

	const checked: 'indeterminate' | boolean = isIndeterminate ? 'indeterminate' : isAllSelected;

	const handleSelectAll = useCallback(
		({ checked }: CheckboxCheckedChangeDetails) => {
			setSelectedIds(checked ? new Set(allIds) : new Set());
		},
		[ allIds ],
	);

	const handleSelectRow = useCallback((id: T) => {
		return ({ checked }: CheckboxCheckedChangeDetails) => {
			setSelectedIds((prev) => {
				const next = new Set(prev);

				if (checked) next.add(id);
				else next.delete(id);

				return next;
			});
		};
	}, []);

	return {
		selected,
		headerSelection: {
			checked:         checked,
			onCheckedChange: handleSelectAll,
		},
		rowSelection: (id: number | string) => ({
			checked:         isSelected(id as T),
			onCheckedChange: handleSelectRow(id as T),
		}),
	};
};
